import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export interface SystemNotification {
  id: number;
  title: string;
  content: string;
  category_id: number | null;
  type: 'maintenance' | 'announcement' | 'emergency' | 'advertisement';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  target_areas: number[];
  target_users: number[];
  channels: string[];
  category?: NotificationCategory;
  deliveries?: NotificationDelivery[];
}

export interface NotificationCategory {
  id: number;
  name: string;
  description: string | null;
}

export interface NotificationDelivery {
  id: number;
  notification_id: number;
  user_id: number;
  channel: string;
  status: string;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
}

interface NotificationState {
  notifications: SystemNotification[];
  categories: NotificationCategory[];
  loading: boolean;
  error: string | null;
  total: number;
  unreadCount: number;
}

const initialState: NotificationState = {
  notifications: [],
  categories: [],
  loading: false,
  error: null,
  total: 0,
  unreadCount: 0
};

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (params: { page?: number; category?: number; type?: string; priority?: string; area?: number }) => {
    const response = await api.get('/notifications', { params });
    return response.data;
  }
);

export const fetchUserNotifications = createAsyncThunk(
  'notifications/fetchUserNotifications',
  async (params: { page?: number; unread?: boolean }) => {
    const response = await api.get('/notifications/user', { params });
    return response.data;
  }
);

export const createNotification = createAsyncThunk(
  'notifications/create',
  async (data: Partial<SystemNotification>) => {
    const response = await api.post('/notifications', data);
    return response.data;
  }
);

export const updateNotification = createAsyncThunk(
  'notifications/update',
  async ({ id, data }: { id: number; data: Partial<SystemNotification> }) => {
    const response = await api.put(`/notifications/${id}`, data);
    return response.data;
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (id: number) => {
    await api.delete(`/notifications/${id}`);
    return id;
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (id: number) => {
    await api.post(`/notifications/${id}/read`);
    return id;
  }
);

export const fetchNotificationCategories = createAsyncThunk(
  'notifications/fetchCategories',
  async () => {
    const response = await api.get('/notifications/categories');
    return response.data;
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload.data;
        state.total = action.payload.total;
        state.loading = false;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch notifications';
      })
      .addCase(createNotification.fulfilled, (state, action) => {
        state.notifications.unshift(action.payload);
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        const index = state.notifications.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.notifications[index] = action.payload;
        }
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.notifications = state.notifications.filter(n => n.id !== action.payload);
      })
      .addCase(fetchNotificationCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload.data;
        state.total = action.payload.total;
        state.unreadCount = action.payload.data.filter(
          (n: SystemNotification) => !n.deliveries?.some(d => d.read_at)
        ).length;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && notification.deliveries) {
          notification.deliveries = notification.deliveries.map(d => ({
            ...d,
            read_at: d.read_at || new Date().toISOString()
          }));
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });
  }
});

export default notificationSlice.reducer;